/**
 * Cliente de Firebase de la web publica.
 *
 * Tres cosas que este modulo garantiza y que el resto de la interfaz da por hechas:
 *
 * 1. **Contra emuladores en desarrollo, siempre** (ADR-032). El proyecto local es
 *    `demo-padel-local`: el prefijo `demo-` lo reserva Firebase para proyectos que no
 *    existen en la nube, asi que ni siquiera hay un destino real al que escaparse.
 * 2. **Sesion anonima antes de cualquier callable** (ADR-027). No identifica al cliente
 *    —el cliente real es la cedula—; sirve para App Check y para las cuotas antiabuso.
 * 3. **App Check preparado para las dos puntas** (ADR-028). En produccion, reCAPTCHA
 *    Enterprise con la clave de sitio del entorno. En local, nada: contra un proyecto
 *    `demo-` no hay atestacion posible y las funciones tampoco lo exigen porque la Suite
 *    de Emuladores define `FUNCTIONS_EMULATOR`. El unico puente admitido entre ambos es
 *    el **token de depuracion** documentado, que se toma de `VITE_APPCHECK_DEBUG_TOKEN`
 *    y jamas se versiona.
 */

import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { ReCaptchaEnterpriseProvider, initializeAppCheck, type AppCheck } from 'firebase/app-check';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';

import type { ConfiguracionApp } from '@/configuracion/entorno';

declare global {
  // Nombre impuesto por el SDK de App Check: se lee de `self` antes de inicializarlo.
  var FIREBASE_APPCHECK_DEBUG_TOKEN: string | boolean | undefined;
}

/**
 * Proyecto de desarrollo local. Identico al de `scripts/emulador.mjs` y al de
 * `npm run emulators`. Si cambia uno hay que cambiar los tres.
 */
export const PROYECTO_EMULADOR = 'demo-padel-local';

/**
 * Region de las funciones. `southamerica-east1` es la region de Google Cloud mas cercana
 * a Uruguay; tiene que coincidir con `REGION_FUNCIONES` de `functions/src/infra/entorno.ts`.
 */
export const REGION_FUNCIONES = 'southamerica-east1';

/** Puertos de la Suite de Emuladores. Iguales a los de `firebase.json`. */
export const PUERTOS_EMULADOR = { auth: 9099, firestore: 8080, funciones: 5001 } as const;

/** Servicios ya conectados y listos para usar. */
export interface ServiciosFirebase {
  readonly app: FirebaseApp;
  readonly funciones: Functions;
  readonly usarEmuladores: boolean;
  readonly emuladoresHost: string;
  /** `null` en modo emulador: no hay App Check contra un proyecto `demo-`. */
  readonly appCheck: AppCheck | null;
}

/**
 * Credenciales para el modo emulador.
 *
 * No son secretas ni reales: el emulador de Auth acepta cualquier `apiKey` no vacia y
 * `demo-padel-local` no existe en la nube. Se dejan explicitas y evidentes para que
 * nadie las confunda con configuracion de produccion (CLAUDE.md §5).
 */
function credencialesDeEmulador(): { apiKey: string; projectId: string; appId: string } {
  return {
    apiKey: 'clave-de-emulador-no-es-un-secreto',
    projectId: PROYECTO_EMULADOR,
    appId: `1:0:web:${PROYECTO_EMULADOR}`,
  };
}

function inicializarApp(configuracion: ConfiguracionApp): FirebaseApp {
  if (getApps().length > 0) {
    return getApp();
  }
  return initializeApp(
    configuracion.firebase === null ? credencialesDeEmulador() : { ...configuracion.firebase },
  );
}

function configurarAppCheck(app: FirebaseApp, configuracion: ConfiguracionApp): AppCheck | null {
  if (configuracion.appCheckDebugToken !== null) {
    // Mecanismo documentado de depuracion: hay que fijarlo ANTES de inicializar.
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = configuracion.appCheckDebugToken;
  }

  if (configuracion.appCheckSiteKey === null) {
    // Modo emulador. Las callables no exigen App Check porque la Suite define
    // FUNCTIONS_EMULATOR (docs/API.md §0), y contra un proyecto `demo-` no hay backend
    // de App Check con el que atestiguar.
    return null;
  }

  return initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(configuracion.appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/** Servicios memorizados: `initializeApp` y `connect*Emulator` no se repiten. */
let memoria: ServiciosFirebase | null = null;

/** Construye —una sola vez— los servicios de Firebase de la aplicacion. */
export function crearServicios(configuracion: ConfiguracionApp): ServiciosFirebase {
  if (memoria !== null) {
    return memoria;
  }

  const app = inicializarApp(configuracion);
  const appCheck = configurarAppCheck(app, configuracion);
  const funciones = getFunctions(app, REGION_FUNCIONES);

  if (configuracion.usarEmuladores) {
    const host = configuracion.emuladoresHost;
    connectFunctionsEmulator(funciones, host, PUERTOS_EMULADOR.funciones);
  }

  memoria = {
    app,
    funciones,
    usarEmuladores: configuracion.usarEmuladores,
    emuladoresHost: configuracion.emuladoresHost,
    appCheck,
  };
  return memoria;
}
